import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { notificationsApi, type NotificationSettings } from '@/api/notifications';
import { NotificationFeed } from '@/components/notifications/NotificationFeed';
import { useMarkAllNotificationsRead } from '@/hooks/useMarkAllNotificationsRead';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';
import { useHasFeature } from '@/hooks/useHasFeature';

// TZ §10.2 — student-facing toggle groups.
// Labels are now resolved via i18n at render time (see CHANNEL_LABEL_KEYS below).
// Тип — Partial: канал «Оплата» сервер по-прежнему знает, а ученику мы его не
// показываем. Перечисляем только то, что рисуем.
const CHANNEL_LABEL_KEYS: Partial<Record<keyof NotificationSettings['channels'], string>> = {
  lessons: 'notifications.channelLessons',
  chat: 'notifications.channelChat',
  speaking: 'notifications.channelSpeaking',
  // Канал «Оплата» убран у ученика 01.09.2026: платежей в интерфейсе нет,
  // а слово на экране настроек — лишний повод для проверяющего.
  homework: 'notifications.channelHomework',
};

// TZ §10.2 — критические уведомления нельзя отключить
// (ДЗ проверено, оплата, истечение доступа).
const CRITICAL_CHANNELS: ReadonlyArray<keyof NotificationSettings['channels']> = ['payments', 'homework'];

export function StudentNotificationsPage() {
  const { t } = useTranslation();
  // Без права chat нет и канала «Чат» (14.09.2026): чата у такого ученика нет.
  const canChat = useHasFeature('chat');
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ['notifications', 'settings'], queryFn: notificationsApi.getSettings });

  // Лента — общий компонент с кураторской страницей (11.09.2026): строка,
  // отметка прочтения и пустое состояние живут в одном месте.
  const markAll = useMarkAllNotificationsRead();
  const updateSettings = useMutation({
    mutationFn: notificationsApi.updateSettings,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', 'settings'] }),
  });

  const toggle = (path: keyof NotificationSettings | `channels.${keyof NotificationSettings['channels']}`) => {
    if (!settings.data) return;
    const next = { ...settings.data, channels: { ...settings.data.channels } };
    if (path === 'email_enabled' || path === 'push_enabled') {
      next[path] = !next[path];
    } else {
      const key = path.split('.')[1] as keyof NotificationSettings['channels'];
      if (CRITICAL_CHANNELS.includes(key)) return; // cannot disable critical groups
      next.channels[key] = !next.channels[key];
    }
    updateSettings.mutate(next);
  };

  return (
    <>
      <PageHeader
        title={t('notifications.title')}
        actions={
          <Button variant="ghost" onClick={() => markAll.mutate()} loading={markAll.isPending}>
            <span className="material-symbols-outlined">mark_email_read</span>
            {t('notifications.markAllRead')}
          </Button>
        }
      />

      <Card variant="default" className="mb-6">
        <CardTitle>{t('notifications.settings')}</CardTitle>
        {settings.isError ? (
          <p className="mt-2 text-sm text-error">{humanizeError(settings.error)}</p>
        ) : settings.data ? (
          <ul className="mt-4 space-y-3">
            <SettingRow label={t('notifications.pushEnabled')} checked={settings.data.push_enabled} onChange={() => toggle('push_enabled')} />
            <SettingRow label={t('notifications.emailEnabled')} checked={settings.data.email_enabled} onChange={() => toggle('email_enabled')} />
            <hr className="border-outline-variant/20" />
            {/* ⚠️ В приложении из магазина канал «Оплата» НЕ показываем. Оплата
                у нас офлайн, в школе: в приложении нет ни цен, ни покупок, ни
                ссылок на них. Переключатель с таким названием намекает
                проверяющему Apple на оплату мимо неё — то есть ровно на то,
                из-за чего отклонили сборку 39. Настройка остаётся на сервере и
                на сайте, скрыт только вид. */}
            {/* ⚠️ Канала «Оплата» здесь больше нет ВОВСЕ (01.09.2026). Раньше он
                прятался только в магазинной сборке, а на сайте ученик его видел —
                и проверяющий, заходящий туда тем же демо-доступом, тоже. */}
            {(['lessons', 'chat', 'speaking', 'homework'] as const)
              .filter((c) => c !== 'chat' || canChat)
              .map((c) => (
              <SettingRow
                key={c}
                label={t(CHANNEL_LABEL_KEYS[c] ?? c)}
                checked={settings.data!.channels[c]}
                onChange={() => toggle(`channels.${c}`)}
                hint={CRITICAL_CHANNELS.includes(c) ? t('notifications.criticalHint') : undefined}
                disabled={CRITICAL_CHANNELS.includes(c)}
              />
            ))}
            {updateSettings.isError && (
              <li className="text-xs text-error">{humanizeError(updateSettings.error)}</li>
            )}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-on-surface-variant">{t('common.loading')}</p>
        )}
      </Card>

      <h2 className="mb-3 font-display text-2xl font-bold tracking-display">{t('notifications.feed')}</h2>
      {markAll.isError && (
        <Card variant="subtle" className="mb-2 text-sm text-error">
          {humanizeError(markAll.error)}
        </Card>
      )}
      <NotificationFeed role="student" />
    </>
  );
}

function SettingRow({
  label,
  checked,
  onChange,
  hint,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-sm text-on-surface">
        {label}
        {hint && <span className="ml-2 text-xs text-on-surface-variant">{hint}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={onChange}
        className={cn(
          'h-7 w-12 rounded-pill transition',
          checked ? 'bg-primary' : 'bg-surface-container-high',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <span
          className={cn(
            'block h-6 w-6 rounded-full bg-surface-container-lowest transition',
            checked ? 'translate-x-5' : 'translate-x-0.5',
          )}
        />
      </button>
    </li>
  );
}
